import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

/**
 * The Aperçu's action bar — « Pré-auth/Barre d'action » (Figma `522:28647`).
 *
 * ## Why it is fixed on mobile and not on desktop
 *
 * The frame's note: the Aperçu is ~2 500px tall, so burying the call to action at the bottom of the
 * scroll puts it out of one-handed reach at 360px. It is pinned to the viewport below `lg` and sits
 * at the end of the content on desktop, where the whole page is closer to hand.
 *
 * ## « Revenir au site » is as visible as the CTA, and confirms nothing
 *
 * US-E11-01: *"the path onward is obvious, and so is the path away — nothing traps them."* Leaving
 * costs nothing because nothing was saved, so a confirmation dialog here would be a lie about the
 * stakes.
 *
 * ## Closed rounds have NO call to action
 *
 * Not a disabled one — absent (US-W0-03, and the frame states the rule explicitly). A control that
 * can only fail is worse on a public page than no control, and the accompanying line explains why
 * it is gone.
 */
export function PreviewActionBar({
  isOpen,
  roundId,
}: {
  isOpen: boolean;
  roundId: string;
}) {
  const { t } = useTranslation("acquire");

  return (
    <div className="sticky bottom-0 z-10 flex flex-col gap-xxs border-t border-(--border-default) bg-surface-card px-md pt-sm pb-lg sm:px-xl lg:static lg:border-t-0 lg:bg-transparent lg:px-0 lg:pb-0">
      {isOpen ? (
        /*
          A real `<Link>`, not a Button with an onPress: this is navigation, so it must be
          middle-clickable and copyable. Styled as a primary button rather than using the Button
          primitive, which renders a real `<button>` — and a button inside an anchor is invalid HTML.

          It goes to REGISTER, not to the application. The `apply_` prefix on the route FILE is a
          layout-escape directive, not part of the path, so the URL is `/apply/{id}/register`. If the
          visitor is already signed in, that route's own guard sends them straight on to their
          application — so this one link is correct for both a first-time reader and a returning one.
        */
        <Link
          to="/apply/$roundId/register"
          params={{ roundId }}
          className="inline-flex min-h-11 w-full items-center justify-center rounded-full border border-transparent bg-brand-600 px-lg py-2.5 text-md-semibold text-(--text-on-brand) outline-none transition-colors hover:bg-brand-700 focus-visible:ring-2 focus-visible:ring-focus-ring"
        >
          {t("preview.action.start")}
        </Link>
      ) : (
        <p className="pb-xs text-center text-sm-regular text-(--text-secondary)">
          {t("preview.action.closed-note")}
        </p>
      )}

      {/* Underlined, full-width target — the frame gives it the same 44px height as the CTA. */}
      <Link
        to="/website"
        className="inline-flex min-h-11 w-full items-center justify-center rounded-md text-sm-medium text-(--text-secondary) underline outline-none hover:text-(--text-primary) focus-visible:ring-2 focus-visible:ring-focus-ring"
      >
        {t("preview.action.back-to-site")}
      </Link>
    </div>
  );
}
